import React, { useEffect, useRef, useState, useCallback, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import LoadingScreen from '../components/LoadingScreen';
import { FaArrowUp, FaGlobe, FaMobileAlt, FaRocket, FaLock, FaServer, FaMagic } from "react-icons/fa";

const Spline = lazy(() => import("@splinetool/react-spline"));

// Separate component for the motion effect with memoization
const FloatingEffect = React.memo(({ isDarkMode }) => {
  const mouseRef = useRef({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: -999, y: -999 });
  const [isShow, setShow] = useState(false);
  const rafRef = useRef();

  const updatePosition = useCallback(() => {
    setPosition(prev => ({
      x: prev.x + (mouseRef.current.x - 200 - prev.x) * 0.1,
      y: prev.y + (mouseRef.current.y - 200 - prev.y) * 0.1
    }));
    rafRef.current = requestAnimationFrame(updatePosition);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(rafRef.current);
  }, [updatePosition]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const isDark = document.documentElement.classList.contains("dark");
      const aboutSection = document.getElementById("about");
      const contactSection = document.getElementById("contact");
      
      const aboutRect = aboutSection?.getBoundingClientRect();
      const contactRect = contactSection?.getBoundingClientRect();
      
      const inAbout = aboutRect && e.clientY >= aboutRect.top && e.clientY <= aboutRect.bottom;
      const inContact = contactRect && e.clientY >= contactRect.top && e.clientY <= contactRect.bottom;
      
      if (isDark || inAbout || inContact) {
        setShow(true);
        mouseRef.current = { x: e.clientX, y: e.clientY };
      } else {
        setShow(false);
      }
    };

    const throttledMouseMove = throttle(handleMouseMove, 16); // ~60fps
    window.addEventListener("mousemove", throttledMouseMove);
    
    return () => {
      window.removeEventListener("mousemove", throttledMouseMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <motion.div
      className={`fixed pointer-events-none ${isShow ? "block" : "hidden"}`}
      style={{
        width: "400px",
        height: "400px",
        borderRadius: "50% 22% 40% 80%",
        filter: "blur(100px)",
        background: isDarkMode
          ? "linear-gradient(#43d9ad, #4d5bce)"
          : "linear-gradient(#F9FD03, #FFC107)",
        opacity: isDarkMode ? 0.4 : 1,
        zIndex: 2,
        transform: `translate(${position.x}px, ${position.y}px)`
      }}
    />
  );
});

// Throttle function for performance
function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function() {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const Content = React.memo(({ isDarkMode }) => {
  const [ref, inView] = useInView({ triggerOnce: false, threshold: 0.1 });
  const [isHovered, setIsHovered] = useState(false);
  const [isHovered3, setIsHovered3] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const mousePosition = useRef({ x: 0, y: 0 });

  const servicesData = [
    {
      name: "Web Development",
      icon: <FaGlobe />,
      description: "Building responsive and interactive websites with modern frameworks like Laravel, Wordpress, React JS, and Angular"
    },
    {
      name: "Mobile Application",
      icon: <FaMobileAlt />,
      description: "Developing cross-platform mobile apps for iOS and Android using Flutter and React Native"
    },
    {
      name: "API Development",
      icon: <FaRocket />,
      description: "Creating robust RESTful and GraphQL APIs for your applications using Laravel, Node.js, and Express"
    },
    {
      name: "Cyber Security",
      icon: <FaLock />,
      description: "Implementing security best practices and vulnerability testing for web applications, networks, and systems"
    },
    {
      name: "Server Management",
      icon: <FaServer />,
      description: "Cloud infrastructure setup, deployment, and maintenance on AWS, Azure, and DigitalOcean"
    },
    {
      name: "Other Services",
      icon: <FaMagic />,
      description: "UI/UX design, consulting, and custom software solutions tailored to your needs"
    },
  ];

  const handleMouseMove = useCallback((e) => {
    mousePosition.current = { 
      x: e.clientX / 30, 
      y: e.clientY / 30 
    };
  }, []);

  // Add scroll event listener to show/hide the button
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <main
      id="home"
      className={`flex-grow ${
        isDarkMode ? "bg-[#212529] text-white" : "bg-white text-black"
      }`}
    >
      <LoadingScreen splineReady={splineLoaded} />
      <FloatingEffect isDarkMode={isDarkMode} />

      {/* Hero Section */}
      <section className={`relative h-screen flex flex-col items-center justify-center text-center overflow-hidden ${
        isDarkMode ? "bg-black" : "bg-white"
      }`}>
        <div className="relative z-10">
          <div className="relative w-full z-10" style={{ marginLeft: "30px" }}>
            <div className="absolute top-1/2 left-1/2 w-[450px] h-[350px] bg-gradient-to-b from-black/40 to-transparent rounded-xl blur-[100px] opacity-60 pointer-events-none transform -translate-x-1/2 -translate-y-1/2" />
            <Suspense fallback={
              <div className="relative mx-auto bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" 
                   style={{ height: "350px", width: "450px" }} />
            }>
              <Spline
                scene="https://prod.spline.design/52k2ltn6SLrVy5Oa/scene.splinecode"
                className="relative mx-auto"
                style={{ height: "350px", width: "450px" }}
                onLoad={() => setSplineLoaded(true)}
              />
            </Suspense>
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold">
            Welcome, Hooman!
          </h1>
          <p className="mt-4 text-lg">
            Just showing my simple portfolio
          </p>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        ref={ref}
        className={`min-h-screen flex flex-col md:flex-row items-center justify-center text-white px-6 py-20 gap-12 overflow-hidden ${
          isDarkMode ? "bg-[#212529]" : "bg-[#00AA71]"
        }`}
      >
        <div className="md:w-1/2 text-center md:text-left p-10">
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ type: "spring", stiffness: 100, damping: 10 }}
          >
            <motion.h2
              className="text-4xl font-bold text-white mb-4 relative"
              style={{ zIndex: "10" }}
              animate={isHovered ? { rotateZ: -10, y: -50 } : { rotateZ: 0, y: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 12 }}
            >
              About Me
            </motion.h2>

            <motion.p
              className="max-w-2xl text-lg text-white relative cursor-pointer"
              style={{ zIndex: "10" }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              Hi! I'm a Laravel & Flutter expert with comprehensive experience in MERN & MEAN stack development. I specialize in end-to-end project management, from concept to deployment, with proven expertise in server administration and DevOps workflows.
            </motion.p>

            <motion.div 
              className="mt-8 p-6 bg-white border border-[#F9FD04] relative rounded-2xl shadow-lg border-[#FFC107] border-4"
              style={{ zIndex: "10" }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <h3 className="text-xl font-semibold text-gray-800">More About Me</h3>
              <p className="text-gray-600 mt-2">
                As a seasoned project leader, I manage the complete development lifecycle including staging, development, and production environments. I handle deployments across Google Play, App Store, and web platforms, ensuring seamless delivery of high-quality applications. My expertise in version control with Git and systematic project management has consistently delivered successful projects from scratch to production.
              </p>
            </motion.div>
          </motion.div>
        </div>

        <div className="md:w-1/2 flex justify-center">
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ type: "spring", stiffness: 100, damping: 10, delay: 0.2 }}
            whileHover={{ scale: 1.05, rotate: 3 }}
            className="relative"
            style={{ zIndex: "10" }}
          >
            <motion.img
              src="img/me_head.png"
              alt="About Me"
              className="rounded-lg cursor-pointer w-[400px]"
              whileHover={{ scale: 1.1, rotate: -2 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="relative min-h-screen flex items-center justify-center bg-fixed bg-cover bg-center py-20"
        style={{ backgroundImage: isDarkMode ? "url('img/bg1.jpg')" : "url('img/bg2.jpg')" }}
      >
        <div className={`absolute inset-0 ${isDarkMode ? "bg-black bg-opacity-90" : "bg-white bg-opacity-90"}`}></div>
        
        <div className="container mx-auto relative z-10 px-4">
          <motion.h2 
            className="text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: -50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            My Services
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service, index) => (
              <motion.div
                key={index}
                style={{ height: "250px" }}
                className={`p-8 rounded-xl shadow-lg border-2 ${
                  isDarkMode 
                    ? "bg-gray-800 border-[#43d9ad]" 
                    : "bg-white border-[#00AA71]"
                }`}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -10, 
                  boxShadow: isDarkMode 
                    ? "0 10px 25px -5px rgba(67, 217, 173, 0.4)" 
                    : "0 10px 25px -5px rgba(0, 170, 113, 0.4)",
                  borderWidth: "4px"
                }}
              >
                <div className="text-5xl mb-6 flex justify-center text-[#ffc107]">{service.icon}</div>
                <h3 className={`text-2xl font-bold mb-4 text-center ${
                  isDarkMode ? "text-[#43d9ad]" : "text-[#00AA71]"
                }`}>
                  {service.name}
                </h3>
                <p className={`text-center ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
          >
            <p className={`text-lg mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
              I provide comprehensive solutions tailored to your specific needs.
            </p>
            <motion.button
              className={`px-8 py-3 rounded-lg font-semibold ${
                isDarkMode 
                  ? "bg-[#00AA71] text-white hover:bg-[#43d9ad] hover:text-black" 
                  : "bg-[#00AA71] text-white hover:bg-[#F9FD03] hover:text-black"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = "#contact"}
            >
              Get In Touch
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className={`min-h-screen flex flex-col md:flex-row items-center justify-center text-white px-6 py-20 gap-12 ${
          isDarkMode ? "bg-[#212529]" : "bg-[#00AA71]"
        }`}
        onMouseMove={handleMouseMove}
      >
        <div className="md:w-1/2 w-full max-w-lg flex justify-center items-center z-10" style={{"height": "330px", "overflow": "hidden"}}>
          <Suspense fallback={
            <div className="relative mx-auto bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-full" 
                 style={{"height": "450px"}} />
          }>
            <Spline
              scene="https://prod.spline.design/lRHk-BCpH2IT3n4h/scene.splinecode"
              className="relative mx-auto"
              style={{"height": "450px", "width": "100%"}}
              onLoad={() => setSplineLoaded(true)}
            />
          </Suspense>
        </div>

        <motion.div 
          className={`md:w-1/2 p-8 rounded-xl shadow-lg w-full max-w-lg relative z-10 ${isDarkMode ? "bg-[#212529] border-8 border-[#00AA71]" : "bg-white"}`}
          onMouseEnter={() => setIsHovered3(true)}
          onMouseLeave={() => setIsHovered3(false)}
        >
          <motion.h2
            className={`text-3xl font-bold text-center mb-6 ${isDarkMode ? "text-white" : "text-black"}`}
            style={{ zIndex: "10" }}
            animate={isHovered3 ? { rotateZ: -5, y: 0 } : { rotateZ: 0, y: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 12 }}
          >
            Connect With Me
          </motion.h2>

          <div className="grid grid-cols-2 gap-4">

            {/* LinkedIn */}
            <motion.a
              href="https://www.linkedin.com/in/hernie/"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center p-4 rounded-lg ${isDarkMode ? "bg-blue-800 hover:bg-blue-700" : "bg-blue-500 hover:bg-blue-400"} text-white transition-colors`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex flex-col items-center">
                <svg className="w-8 h-8 mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                <span>LinkedIn</span>
              </div>
            </motion.a>

            {/* GitHub */}
            <motion.a
              href="https://github.com/herndev"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center p-4 rounded-lg ${isDarkMode ? "bg-gray-800 hover:bg-gray-700" : "bg-gray-700 hover:bg-gray-600"} text-white transition-colors`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex flex-col items-center">
                <svg className="w-8 h-8 mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                <span>GitHub</span>
              </div>
            </motion.a>

            {/* TryHackMe */}
            <motion.a
              href="https://tryhackme.com/p/knov"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center p-4 rounded-lg ${isDarkMode ? "bg-[#212C42] hover:bg-[#1a2436]" : "bg-[#212C42] hover:bg-[#1a2436]"} text-white transition-colors`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex flex-col items-center">
                <svg className="w-8 h-8 mb-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10.705 0C7.54 0 4.902 2.285 4.349 5.291a4.525 4.525 0 0 0-4.107 4.5 4.525 4.525 0 0 0 4.52 4.52h6.761a.625.625 0 0 0 0-1.25H4.761a3.273 3.273 0 0 1-3.27-3.27 3.273 3.273 0 0 1 3.27-3.27h.07a.625.625 0 0 0 .624-.574c.244-2.57 2.406-4.571 4.993-4.696h.257c2.749 0 5.103 2.178 5.103 4.977a.54.54 0 0 0 .158.378.625.625 0 0 0 1.09-.418c0-3.593-2.895-6.186-6.351-6.186zm10.79 6.375a.625.625 0 0 0-.625.625v5.469a3.273 3.273 0 0 1-3.27 3.27h-6.762a.625.625 0 0 0 0 1.25h6.761a4.525 4.525 0 0 0 4.52-4.52V7a.625.625 0 0 0-.625-.625zM10.79 7.83a.625.625 0 0 0-.626.625v5.469a.625.625 0 0 0 1.25 0V8.455a.625.625 0 0 0-.625-.625zm3.098 0a.625.625 0 0 0-.625.625v5.469a.625.625 0 0 0 1.25 0V8.455a.625.625 0 0 0-.625-.625z"/>
                </svg>
                <span>TryHackMe</span>
              </div>
            </motion.a>

            {/* Certifications - Vibrant Yellow */}
            <motion.a
              href="#" // Replace with your link
              onClick={() => alert("Coming Soon!")}
              rel="noopener noreferrer"
              className={`flex items-center justify-center p-4 rounded-lg ${isDarkMode ? "bg-[#F9FD04] hover:bg-[#D9DD00]" : "bg-[#F9FD04] hover:bg-[#D9DD00]"} text-black transition-colors`} // Black text for contrast
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex flex-col items-center">
                <svg 
                  className="w-8 h-8 mb-2" 
                  fill="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 3C2.89 3 2 3.89 2 5V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V5C22 3.89 21.11 3 20 3H4M18 5V15L15.5 13.5L13 15V5H18M11 5V11L8.5 9.5L6 11V5H11M4 5H6V13L8.5 11L11 13V19H4V5Z" />
                </svg>
                <span>Certifications</span>
              </div>
            </motion.a>
          </div>

          {/* Telegram Button */}
          <motion.a
            href="https://t.me/herndev"
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-6 flex items-center justify-center p-3 rounded-lg ${isDarkMode ? "bg-[#0088CC] hover:bg-[#0077B5]" : "bg-[#0088CC] hover:bg-[#0077B5]"} text-white font-semibold transition-colors w-full`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
            </svg>
            Message Me on Telegram
          </motion.a>
        </motion.div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollButton && (
        <motion.button
          onClick={scrollToTop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className={`fixed bottom-8 right-8 p-4 rounded-full shadow-lg z-50 ${
            isDarkMode 
              ? "bg-[#43d9ad] text-gray-600 hover:bg-[#0C9367] hover:text-white" 
              : "bg-[#00AA71] text-white hover:bg-[#F9FD03] hover:text-black"
          }`}
          aria-label="Scroll to top"
        >
          <FaArrowUp className="text-xl" />
        </motion.button>
      )}
    </main>
  );
});

export default Content;